import { effectScope } from 'vue'
import { useConfig } from '@/composables/useConfig'
import { useCurUser } from '@/composables/useCurUser'

export const valuerAppLinks = {
  GENERAL: 'general',
  DUPLICATES: 'duplicates',
  ANALYTICS: 'analytics',
  REFERENCE_MAP: 'reference-map',
  EXTERIOR: 'exterior',
  INTERIOR: 'interior',
  COMMONS: 'commons',
  SURROUNDINGS: 'surroundings',
  VALUATION: 'valuation',
  EXTRA_INFO: 'extra-info',
  DOCUMENTS: 'documents',
  DOCUMENT: 'document',
}

const expertRoutesBeforeEnter = () => (to, _from, next) => {
  if (!to.params.userId) {
    const { userId } = useCurUser()

    if (userId.value) {
      return next({ ...to, params: { ...to.params, userId: userId.value } })
    }
  }

  return next()
}

const valuationRoutes = [
  {
    name: 'dvm',
    redirect: {
      name: 'valuation.request',
    },
    path: '/dvm',
  },
  {
    name: 'valuation',
    redirect: {
      name: 'valuation.request',
      params: {
        action: 'start',
      },
    },
    path: '/valuation',
  },
  {
    name: 'valuation.request',
    component: () => import('@/flows/request-submission/RequestSubmissionFlow.vue'),
    path: '/valuation/request/:action?',
    // beforeEnter: (to, from, next) => {
    //     console.debug('valuation.request.beforeEnter', to, from)
    // },
    // TODO: split into separate routes per action, and add meta to first step:
    // meta: {
    //     resetCurRequest: true,
    // },
  },
  {
    name: 'valuation.edit',
    path: '/valuation/request/edit',
    redirect: { name: 'valuation.request' },
    // beforeEnter: (to, from, next) => {
    //     // NOTE: logic moved inside `valuation.request` since we need the request to be loaded
    //     // let action = 'extra-info'
    //     // if (!store.state.valuationStore.Address.features.f_building_type) {
    //     //     action = 'building-type'
    //     // } else if (
    //     //     ['apartment', 'house'].includes(
    //     //         store.state.valuationStore.Address.features.f_building_type
    //     //     )
    //     // ) {
    //     //     if (!store.state.valuationStore.Address.address.streetname) {
    //     //         action = 'address'
    //     //     } else if (!store.state.valuationStore.Address.features.building_id) {
    //     //         // TODO: add match_address here
    //     //         console.warn('must run match_address here')
    //     //         // await store.dispatch('valuationStore/Address/match_address')
    //     //         action = 'confirm-main-building'
    //     //     }
    //     // }
    //     return next({
    //         ...to,
    //         name: 'valuation.request',
    //     })
    // },
  },
  {
    name: 'expert',
    path: '/expert',
    component: () => import('./views/expert/ValuerSettings.vue'),
    redirect: { name: 'expert.dashboard' },
    children: [
      {
        name: 'expert.dashboard',
        path: ':userId?/dashboard',
        component: () => import('./views/Dashboard/Views/ValuerDashboard.vue'),
        beforeEnter: expertRoutesBeforeEnter(),
      },
      {
        name: 'expert.info',
        path: ':userId?/info',
        component: () => import('./views/expert/ValuerInfo.vue'),
        beforeEnter: expertRoutesBeforeEnter(),
      },
      {
        name: 'expert.availability',
        path: ':userId?/availability',
        component: () => import('./views/expert/ValuerAvailability.vue'),
        beforeEnter: expertRoutesBeforeEnter(),
      },
      {
        name: 'expert.appointments',
        path: ':userId?/appointments',
        component: () => import('./views/expert/ValuerAppointments.vue'),
        beforeEnter: expertRoutesBeforeEnter(),
      },
    ],
  },
  {
    name: 'expert.summary',
    path: '/expert/valuation',
    component: () => import('./views/expert/ExpertSummary.vue'),
  },
  {
    name: 'dispatcher',
    path: '/dispatcher',
    component: () => import('./views/dispatcher/DispatcherTools.vue'),
    meta: {
      resetCurRequest: true,
    },
    children: [
      {
        name: 'dispatcher.remote-valuations',
        path: 'remote-valuations',
        component: () => import('./views/Dashboard/Views/RemoteDispatcherDashboard.vue'),
      },
      {
        name: 'dispatcher.onsite-valuations',
        path: 'onsite-valuations',
        component: () => import('./views/Dashboard/Views/OnsiteDashboard.vue'),
      },
      {
        name: 'dispatcher.experts',
        path: 'experts',
        component: () => import('./views/dispatcher/DispatcherExperts.vue'),
      },
      {
        name: 'dispatcher.availability',
        path: 'availability',
        component: () => import('./views/dispatcher/DispatcherAvailability.vue'),
      },
      {
        name: 'dispatcher.support',
        path: 'support',
        component: () => import('./views/dispatcher/DispatcherSupportTools.vue'),
      },
    ],
  },
  {
    // redirection for backward compatibility (email link redirect to ovm/borrower)
    name: 'valuation.ovm.borrower',
    path: '/valuation/ovm/borrower/:action?',
    redirect: () => {
      return { name: 'valuation.borrower' }
    },
  },
  {
    name: 'valuation.borrower',
    path: '/valuation/borrower/:action?',
    component: () => import('@/flows/borrower/BorrowerFlow.vue'),
  },
  {
    name: 'valuation.onsite.summary',
    path: '/valuation/onsite',
    component: () => import('./views/expert/ExpertSummary.vue'),
  },
  {
    name: 'valuation.dvm',
    component: () => import('@/apps/DVM/Dvm.vue'),
    path: '/valuation/dvm',
    beforeEnter: (to, _from, next) => {
      const scope = effectScope()
      scope.run(() => {
        const config = useConfig()
        const { authenticated } = useCurUser()
        if (!config.ENABLE_DVM || (config.CHECK_LOGIN && !authenticated.value)) {
          console.warn('DVM is disabled or user is not a DVM valuer: Redirecting to dashboard')
          return next({ name: 'dashboard', params: { lang: to.params.lang } })
        }
        return next()
      })
      scope.stop()
    },
  },
  {
    name: 'valuation.report-legacy',
    component: () => import('./views/report/ReportContent.vue'),
    path: '/valuation/report-legacy',
    meta: {
      public: true,
    },
  },
  {
    name: 'valuation.report',
    component: () => import('./views/report_v2/ValuationReport.vue'),
    path: '/valuation/report',
    meta: {
      public: true,
    },
  },
  {
    name: 'valuation.flood-report',
    component: () => {
      const config = useConfig()

      return config.REPORT_BETA
        ? import('./views/report_v2/FloodReport.vue')
        : import('./views/report/FloodReport.vue')
    },
    path: '/valuation/flood-report',
    meta: {
      public: true,
    },
  },
  {
    name: 'valuation.valuer-app',
    component: () => import('../ValuerTool/ValuerTool.vue'),
    path: '/valuation/valuer-app',
    redirect: {
      name: `valuation.valuer-app.${valuerAppLinks.GENERAL}`,
    },
    children: [
      {
        name: `valuation.valuer-app.${valuerAppLinks.GENERAL}`,
        path: valuerAppLinks.GENERAL,
        component: () => import('../ValuerTool/views/General/GeneralPage.vue'),
        meta: {
          mobile: {
            title: `valuer_tool.${valuerAppLinks.GENERAL}.title`,
            showValuerSidebar: true,
          },
        },
      },
      {
        name: `valuation.valuer-app.${valuerAppLinks.ANALYTICS}`,
        path: valuerAppLinks.ANALYTICS,
        component: () => import('../ValuerTool/views/Analytics/AnalyticsPage.vue'),
        meta: {
          mobile: {
            title: `valuer_tool.${valuerAppLinks.ANALYTICS}.title`,
            showValuerSidebar: true,
          },
        },
      },
      {
        name: `valuation.valuer-app.${valuerAppLinks.REFERENCE_MAP}`,
        path: valuerAppLinks.REFERENCE_MAP,
        component: () => import('../ValuerTool/views/RefMap/RefMap.vue'),
        meta: {
          mobile: {
            title: `valuer_tool.${valuerAppLinks.REFERENCE_MAP}.title`,
            showValuerSidebar: true,
          },
        },
      },
      {
        name: `valuation.valuer-app.${valuerAppLinks.EXTERIOR}`,
        path: valuerAppLinks.EXTERIOR,
        component: () => import('../ValuerTool/views/Exterior/ExteriorPage.vue'),
        meta: {
          mobile: {
            title: `valuer_tool.${valuerAppLinks.EXTERIOR}.title`,
            showValuerSidebar: true,
          },
        },
      },
      {
        name: `valuation.valuer-app.${valuerAppLinks.SURROUNDINGS}`,
        path: valuerAppLinks.SURROUNDINGS,
        component: () => import('../ValuerTool/views/Surroundings/SurroundingsAndMobility.vue'),
        meta: {
          mobile: {
            title: `valuer_tool.${valuerAppLinks.SURROUNDINGS}.title`,
            showValuerSidebar: true,
          },
        },
      },
      {
        name: `valuation.valuer-app.${valuerAppLinks.COMMONS}`,
        path: valuerAppLinks.COMMONS,
        component: () => import('../ValuerTool/views/Commons/CommonsPage.vue'),
        meta: {
          mobile: {
            title: `valuer_tool.${valuerAppLinks.COMMONS}.title`,
            showValuerSidebar: true,
          },
        },
      },
      {
        name: `valuation.valuer-app.${valuerAppLinks.DOCUMENTS}`,
        path: valuerAppLinks.DOCUMENTS,
        component: () => import('../ValuerTool/views/Documents/DocumentsSection.vue'),
        meta: {
          isSummaryHidden: true,
          mobile: {
            title: `valuer_tool.${valuerAppLinks.DOCUMENTS}.title`,
            showValuerSidebar: true,
          },
        },
      },
      {
        name: `valuation.valuer-app.${valuerAppLinks.DOCUMENT}`,
        path: `${valuerAppLinks.DOCUMENT}/:docId`,
        component: () => import('../ValuerTool/views/Documents/DocumentViewer.vue'),
        props: true,
        meta: {
          mobile: {
            title: `valuer_tool.${valuerAppLinks.DOCUMENT}.title`,
            showValuerSidebar: true,
          },
          isSummaryHidden: true,
          restoreScroll: true,
        },
      },
      {
        name: `valuation.valuer-app.${valuerAppLinks.INTERIOR}`,
        path: valuerAppLinks.INTERIOR,
        component: () => import('../ValuerTool/views/Interior/InteriorPage.vue'),
        redirect: {
          name: `valuation.valuer-app.${valuerAppLinks.INTERIOR}.info`,
        },
        children: [
          {
            name: `valuation.valuer-app.${valuerAppLinks.INTERIOR}.info`,
            path: 'info',
            component: () => import('../ValuerTool/views/Interior/tabs/InteriorTab.vue'),
            meta: {
              mobile: {
                title: `valuer_tool.${valuerAppLinks.INTERIOR}.title`,
                showValuerSidebar: true,
              },
            },
          },
          {
            name: `valuation.valuer-app.${valuerAppLinks.INTERIOR}.floors`,
            path: 'floors',
            component: () => import('../ValuerTool/views/Interior/tabs/Floorplan/FloorsPage.vue'),
            meta: {
              mobile: {
                title: `valuer_tool.${valuerAppLinks.INTERIOR}.title`,
                showValuerSidebar: true,
              },
            },
          },
          {
            name: `valuation.valuer-app.${valuerAppLinks.INTERIOR}.floorplan`,
            path: 'floorplan',
            redirect: {
              name: `valuation.valuer-app.${valuerAppLinks.INTERIOR}.floorplan.floors`,
            },
            children: [
              {
                name: `valuation.valuer-app.${valuerAppLinks.INTERIOR}.floorplan.rooms`,
                path: 'rooms',
                component: () => import('../ValuerTool/views/Interior/tabs/Floorplan/RoomsPage.vue'),
                meta: {
                  mobile: {
                    title: `valuer_tool.${valuerAppLinks.INTERIOR}.floorplan.rooms.mobile_title`,
                    showValuerSidebar: true,
                  },
                },
              },
            ],
          },
        ],
      },
      {
        name: `valuation.valuer-app.${valuerAppLinks.VALUATION}`,
        path: valuerAppLinks.VALUATION,
        component: () => import('../ValuerTool/views/Valuation/ValuationPage.vue'),
        meta: {
          mobile: {
            title: `valuer_tool.${valuerAppLinks.VALUATION}.title`,
            showValuerSidebar: true,
          },
        },
      },
      {
        name: `valuation.valuer-app.${valuerAppLinks.DUPLICATES}`,
        path: valuerAppLinks.DUPLICATES,
        component: () => import('../ValuerTool/views/Duplicates/DuplicatesPage.vue'),
        meta: {
          mobile: {
            title: `valuer_tool.${valuerAppLinks.DUPLICATES}.title`,
            showValuerSidebar: true,
          },
        },
      },
    ],
  },
  {
    name: 'valuation.dev-payment',
    component: () => import('@/flows/borrower/steps/BorrowerPayment.vue'),
    path: '/valuation/payment/request/:request',
  },
]

export default valuationRoutes
